import {
  Building2,
  ListChecks,
  UserPlus,
  Users,
  Activity,
  Receipt,
  Mail,
  PenLine,
  CheckCircle2,
  Folder,
  BadgeCheck,
  Upload,
  Target,
  UserCheck,
  MessageCircleQuestion,
  type LucideIcon,
} from 'lucide-react';

/**
 * Content for RoleIntroOverlay ("Practice Compass"): a fast, self-drawing map of what this
 * role actually does in HapMetrix, shown once the first time a person holds it.
 *
 * Practice-role values are a deliberate copy of the backend's PRACTICE_ROLES (ADR 0003) -
 * the frontend package cannot import src/lib/roles.ts (see roles.ts here for the same note).
 * `admin` and `observer` have no content: those personas have no walkthrough yet, so
 * useRoleIntro simply skips them.
 */
export type RoleIntroRoleKey =
  | 'owner'
  | 'admin'
  | 'practitioner'
  | 'support_worker'
  | 'client_contact'
  | 'observer';

export interface RoleIntroStep {
  icon: LucideIcon;
  label: string;
  /** Marks an arrival step (client e-signed, permission granted) in the app's `good` colour. */
  good?: boolean;
}

export interface RoleIntroBeat extends RoleIntroStep {
  sub?: string;
  /** This beat expands into `fork` below it, rendered as its own panel. */
  expands?: boolean;
  /** Cue shown under an expanding beat - phrased per-role, never assumed to be "N paths". */
  hint?: string;
}

export interface RoleIntroLane {
  name: string;
  steps: RoleIntroStep[];
}

export interface RoleIntroFork {
  title: string;
  lanes: RoleIntroLane[];
}

export interface RoleIntroContent {
  title: string;
  beats: RoleIntroBeat[];
  fork?: RoleIntroFork;
  tagline: string;
}

export const ROLE_INTRO_CONTENT: Partial<Record<RoleIntroRoleKey, RoleIntroContent>> = {
  owner: {
    title: 'How this works for a practice owner',
    beats: [
      { icon: Building2, label: 'Create your practice', sub: 'One-time setup' },
      { icon: ListChecks, label: 'Fill in the details', sub: 'Guided setup round' },
      { icon: UserPlus, label: 'Onboard a client', expands: true, hint: 'Two paths in' },
      { icon: Users, label: 'Add your team', sub: 'Practitioners and tutors' },
      { icon: Activity, label: 'Cases run', sub: 'Sessions and progress' },
      { icon: Receipt, label: 'Send invoices', sub: 'Billing, one place' },
    ],
    fork: {
      title: 'Onboard a client: two ways in',
      lanes: [
        {
          name: 'New client',
          steps: [
            { icon: Mail, label: 'New enquiry' },
            { icon: PenLine, label: 'Send contract' },
            { icon: CheckCircle2, label: 'Client e-signs', good: true },
            { icon: Folder, label: 'Case created', good: true },
            { icon: UserPlus, label: 'Client invited', good: true },
          ],
        },
        {
          name: 'Existing client',
          steps: [
            { icon: Folder, label: 'Add their case' },
            { icon: UserPlus, label: 'Client invited', good: true },
            { icon: Target, label: 'Create targets' },
            { icon: Users, label: 'Invite tutor', good: true },
          ],
        },
      ],
    },
    tagline:
      'From opening a practice, through enquiries and contracts, to invoices. One flow, start to finish.',
  },
  practitioner: {
    title: 'How this works for a practitioner',
    beats: [
      { icon: BadgeCheck, label: 'Set up your credentials', sub: 'Once, on your profile' },
      { icon: Users, label: 'Added to a case', sub: 'The practice assigns you' },
      { icon: Upload, label: 'Upload assessments', sub: 'Reports and plans' },
      { icon: Target, label: 'Set up case targets', sub: 'What you’ll track' },
      { icon: Activity, label: 'Track and review sessions', sub: 'Data as you go' },
      { icon: CheckCircle2, label: 'Discharge the case', sub: 'When work is done', good: true },
    ],
    tagline: 'From credentials to discharge, everything for a case, in order.',
  },
  client_contact: {
    title: 'How this works for a client',
    beats: [
      { icon: UserPlus, label: 'Create account', sub: 'From your invite' },
      { icon: Activity, label: 'View progress', sub: 'Your case, live' },
      { icon: BadgeCheck, label: 'Want to record sessions too?', expands: true, hint: 'See how' },
      { icon: Receipt, label: 'See invoices', sub: 'Paid outside HapMetrix' },
    ],
    fork: {
      title: 'Becoming a hands-on recorder',
      lanes: [
        {
          name: 'You',
          steps: [
            { icon: Mail, label: 'Ask your practice' },
            { icon: CheckCircle2, label: 'Permission granted', good: true },
            { icon: Activity, label: 'Enter session data', good: true },
          ],
        },
      ],
    },
    tagline:
      'Watching your case is automatic. Recording sessions yourself is opt-in, with your practice’s say-so.',
  },
  support_worker: {
    title: 'How this works for a tutor',
    beats: [
      { icon: UserPlus, label: 'Join a case', expands: true, hint: 'Two ways in' },
      { icon: Activity, label: 'Record sessions', sub: 'Skill and behaviour data' },
    ],
    fork: {
      title: 'Joining a case: two ways in',
      lanes: [
        {
          name: 'Added by your practice',
          steps: [
            { icon: Users, label: 'Practice adds you' },
            { icon: Mail, label: 'You get an invite' },
            { icon: UserPlus, label: 'You create an account' },
            { icon: UserCheck, label: 'You are granted access' },
            { icon: Activity, label: 'Practice enables recording', good: true },
          ],
        },
        {
          name: 'Nominated by a client',
          steps: [
            { icon: MessageCircleQuestion, label: 'Client nominates you' },
            { icon: CheckCircle2, label: 'Practice approves' },
            { icon: Mail, label: 'You get an invite' },
            { icon: UserPlus, label: 'You create an account' },
            { icon: Activity, label: 'Practice enables recording', good: true },
          ],
        },
      ],
    },
    tagline:
      'Two ways onto a case. Recording is a separate step, and your practice decides it.',
  },
};
